<template>
  <div class="summary-overview">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <!-- KPI row -->
    <div class="omx-stat-row summary-overview__kpis">
      <OmxStatTile
          icon="cash-multiple"
          tone="brand"
          :label="$t('project-summary-overview.kpi-expected-end-budget')"
          :value="money(sum.expected_total_fee)"
          :delta="$t('project-summary-overview.kpi-until-today') + ': ' + money(sum.expected_executed_fee)"
          delta-direction="flat" />
      <OmxStatTile
          icon="progress-check"
          tone="purple"
          :label="$t('project-summary-overview.kpi-spent-budget')"
          :value="money(sum.executed_fee)"
          :delta="pct(progressBudget) + ' ' + $t('project-summary-overview.kpi-of-expected')"
          delta-direction="flat" />
      <OmxStatTile
          icon="trending-up"
          :tone="nullToZero(sum.profit) >= 0 ? 'green' : 'warm'"
          :label="$t('project-summary-overview.kpi-current-profit')"
          :delta="$t('project-summary-overview.kpi-end') + ': ' + money(sum.expected_end_profit)"
          :delta-direction="nullToZero(sum.expected_end_profit) >= 0 ? 'up' : 'down'">
        <span :style="profitTextStyle(nullToZero(sum.profit))">{{ money(sum.profit) }}</span>
      </OmxStatTile>
      <OmxStatTile
          icon="chart-arc"
          tone="neutral"
          :label="$t('project-summary-overview.kpi-work-progress')"
          :value="pct(progressWork)"
          :delta="$t('project-summary-overview.kpi-budget') + ' ' + pct(progressBudget)"
          delta-direction="flat" />
    </div>

    <div class="summary-overview__mid">
      <!-- Daily profit chart -->
      <OmxCard density="comfortable"
               :title="$t('project-summary-overview.daily-profit-title')"
               :description="$t('project-summary-overview.daily-profit-desc')"
               class="summary-overview__chart-card">
        <div v-if="chartDays.length" class="chart">
          <div class="chart__plot">
            <div
                v-for="(d, i) in chartDays" :key="i"
                class="chart__bar"
                :class="{
                  'chart__bar--neg': d.total_profit < 0,
                  'chart__bar--sel': selectedIndex === i
                }"
                :style="{ height: barHeight(d.total_profit) }"
                :title="getFormattedDate(d.date) + ' — ' + money(d.total_profit)"
                @click="selectDay(i)">
              <b-icon v-if="!isProjectSummaryHoursValid(d)" icon="alert-circle"
                      size="is-small" type="is-danger" class="chart__warn" />
            </div>
          </div>
          <div class="chart__axis">
            <span>{{ getFormattedDate(chartDays[0].date) }}</span>
            <span v-if="chartDays.length > 1">{{ getFormattedDate(chartDays[chartDays.length - 1].date) }}</span>
          </div>
        </div>
        <p v-else class="summary-overview__empty">{{ $t('project-summary-overview.no-data') }}</p>
      </OmxCard>

      <!-- Hours health -->
      <OmxCard density="comfortable"
               :title="$t('project-summary-overview.hours-health-title')"
               class="summary-overview__health-card">
        <div class="health">
          <div class="health__ring"
               :style="{ background: ringGradient }">
            <div class="health__ring-inner">
              <span class="health__ring-num">{{ validDays }}/{{ activeDays }}</span>
            </div>
          </div>
          <div class="health__legend">
            <div class="health__row">
              <span class="health__chip health__chip--good">{{ validDays }}</span>
              {{ $t('project-summary-overview.days-valid') }}
            </div>
            <div class="health__row" v-if="mismatchDays > 0">
              <span class="health__chip health__chip--bad">{{ mismatchDays }}</span>
              {{ $t('project-summary-overview.days-mismatch') }}
            </div>
          </div>
        </div>
        <div class="health__stats">
          <div class="health__stat">
            <span class="health__stat-val">{{ activeDays }}</span>
            <span class="health__stat-lab">{{ $t('project-summary-overview.working-days') }}</span>
          </div>
          <div class="health__stat">
            <span class="health__stat-val internal-text">{{ getFormattedTimeHour(totalInternalHours) }}</span>
            <span class="health__stat-lab">{{ $t('project-summary-overview.internal-logged') }}</span>
          </div>
          <div class="health__stat">
            <span class="health__stat-val external-text">{{ getFormattedTimeHour(totalExternalHours) }}</span>
            <span class="health__stat-lab">{{ $t('project-summary-overview.external-logged') }}</span>
          </div>
        </div>
      </OmxCard>
    </div>

    <!-- selected day detail (full width, below the row) -->
    <OmxCard v-if="selectedDay" density="comfortable" class="summary-overview__day-card">
      <div class="day-detail__head">
        <div>
          <span class="day-detail__date">{{ getFormattedDate(selectedDay.date) }}</span>
          <b-tag v-if="isProjectSummaryHoursValid(selectedDay)" type="is-success" rounded>
            {{ $t('project-summary-overview.status-on-track') }}
          </b-tag>
          <b-tag v-else type="is-danger" rounded>
            {{ $t('project-summary-overview.days-mismatch') }}
          </b-tag>
        </div>
        <span class="day-detail__profit" :style="profitTextStyle(selectedDay.total_profit)">
          {{ money(selectedDay.total_profit) }}
        </span>
      </div>
      <div class="day-detail__tasks" v-if="selectedDay.tasks && selectedDay.tasks.length">
        <div v-for="(t, ti) in selectedDay.tasks" :key="ti" class="day-detail__task">
          <span class="day-detail__task-title">{{ t.title }}</span>
          <span class="internal-text">{{ getFormattedTimeHour(t.logged_durations_internal) }}</span>
          <span class="external-text">{{ getFormattedTimeHour(t.logged_durations_external) }}</span>
          <span :style="profitTextStyle(t.profit)">{{ money(t.profit) }}</span>
        </div>
      </div>
    </OmxCard>

    <!-- Sortable task table -->
    <OmxCard density="comfortable"
             :title="$t('project-summary-overview.tasks-title')"
             :description="$t('project-summary-overview.tasks-desc')"
             class="summary-overview__tasks-card">
      <b-table
          :data="tasks"
          :hoverable="true"
          :mobile-cards="false"
          default-sort="profit"
          default-sort-direction="desc"
          :show-header="true">
        <b-table-column field="title" :label="$t('project-summary-overview.col-task')" sortable v-slot="props">
          <span class="task-title">{{ props.row.title }}</span>
        </b-table-column>
        <b-table-column field="progress_percentage_quantity" :label="$t('project-summary-overview.col-progress')"
                        sortable numeric v-slot="props">
          <div class="cellbar">
            <div class="cellbar__track">
              <i class="cellbar__fill cellbar__fill--brand"
                 :style="{ width: clampWidth(props.row.progress_percentage_quantity) }"></i>
            </div>
            <span class="cellbar__pct">{{ pct(props.row.progress_percentage_quantity, 0) }}</span>
          </div>
        </b-table-column>
        <b-table-column field="progress_percentage_fee" :label="$t('project-summary-overview.col-budget-used')"
                        sortable numeric v-slot="props">
          {{ pct(props.row.progress_percentage_fee, 0) }}
        </b-table-column>
        <b-table-column field="profit" :label="$t('project-summary-overview.col-profit')"
                        sortable numeric v-slot="props">
          <span :style="profitTextStyle(props.row.profit)">{{ money(props.row.profit) }}</span>
        </b-table-column>
        <b-table-column field="hours" :label="$t('project-summary-overview.col-hours')" v-slot="props">
          <span class="internal-text">{{ getFormattedTime(props.row.logged_internal_durations) }}</span>
          <span class="cell-sep">/</span>
          <span class="external-text">{{ getFormattedTime(props.row.logged_external_durations) }}</span>
        </b-table-column>
        <b-table-column field="status" :label="$t('project-summary-overview.col-status')" v-slot="props">
          <span class="statuschip" :class="'statuschip--' + statusOf(props.row).tone">
            {{ $t('project-summary-overview.' + statusOf(props.row).key) }}
          </span>
        </b-table-column>
      </b-table>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxStatTile from "@/components/OmxStatTile.vue";
import OmxCard from "@/components/OmxCard.vue";

export default {
  name: "ProjectSummaryOverview",
  components: { OmxStatTile, OmxCard },
  props: {
    project_id: { type: String }
  },
  data() {
    return {
      isLoading: false,
      groupedLogs: [],   // from get_project_summary (per-day)
      tasks: [],         // from get_project_summary_by_task
      sum: {},
      selectedIndex: null
    };
  },
  computed: {
    chartDays() {
      // only days with actual logged activity carry a meaningful profit bar
      return this.groupedLogs.filter(d => this.isHavingValue(d));
    },
    selectedDay() {
      return this.selectedIndex != null ? this.chartDays[this.selectedIndex] : null;
    },
    maxAbsProfit() {
      const m = Math.max.apply(Math, this.chartDays.map(d => Math.abs(d.total_profit || 0)));
      return m > 0 ? m : 1;
    },
    activeDays() {
      return this.chartDays.length;
    },
    validDays() {
      return this.chartDays.filter(d => this.isProjectSummaryHoursValid(d)).length;
    },
    mismatchDays() {
      return this.activeDays - this.validDays;
    },
    totalInternalHours() {
      return this.chartDays.reduce((s, d) => s + (d.total_logged_durations_internal_personnel || 0), 0);
    },
    totalExternalHours() {
      return this.chartDays.reduce((s, d) => s + (d.total_logged_durations_external_personnel || 0), 0);
    },
    ringGradient() {
      const p = this.activeDays ? (this.validDays / this.activeDays) * 100 : 0;
      const good = p === 100 ? 'var(--omx-success-600, #1f9d57)' : 'var(--omx-success-600, #1f9d57)';
      const rest = 'var(--omx-danger-600, #d23f3f)';
      // valid arc (green) then mismatch arc (red)
      return `conic-gradient(${good} ${p}%, ${rest} 0)`;
    },
    progressWork() {
      return this.sum && this.sum.expected_total_fee
          ? (this.sum.expected_executed_fee / this.sum.expected_total_fee) * 100 : 0;
    },
    progressBudget() {
      return this.sum && this.sum.expected_total_fee
          ? (this.sum.executed_fee / this.sum.expected_total_fee) * 100 : 0;
    }
  },
  methods: {
    money(value) {
      if (value == null || value === "" || isNaN(value)) return "0 " + this.defaultValues.priceUnit;
      return this.toFixedIfNecessary(value) + " " + this.defaultValues.priceUnit;
    },
    pct(value, dp = 1) {
      if (value == null || value === "" || isNaN(value)) return "0%";
      return this.toFixedIfNecessary(value, dp) + "%";
    },
    nullToZero(value) {
      return (value == null || value === "" || isNaN(value)) ? 0 : value;
    },
    barHeight(profit) {
      const h = (Math.abs(profit || 0) / this.maxAbsProfit) * 100;
      return Math.max(h, 2) + "%";
    },
    clampWidth(value) {
      const v = this.nullToZero(value);
      return Math.max(0, Math.min(v, 100)) + "%";
    },
    selectDay(i) {
      this.selectedIndex = this.selectedIndex === i ? null : i;
    },
    statusOf(row) {
      if (this.nullToZero(row.profit) < 0) return { key: 'status-over-budget', tone: 'bad' };
      if (this.nullToZero(row.expected_end_profit) < 0) return { key: 'status-at-risk', tone: 'warn' };
      return { key: 'status-on-track', tone: 'good' };
    },
    readData() {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const fn = functions.httpsCallable(this.getSubmitEndpoint());
      const perDay = fn({ action: "get_project_summary", document_id: this.project_id, fill_meta: true });
      const perTask = fn({ action: "get_project_summary_by_task", document_id: this.project_id, fill_meta: true });
      Promise.all([perDay, perTask])
          .then(([dayRes, taskRes]) => {
            this.groupedLogs = dayRes.data.grouped_logs || [];
            this.tasks = taskRes.data.grouped_tasks || [];
            this.sum = taskRes.data.sum || {};
            // preselect the most recent active day
            if (this.chartDays.length) {
              this.selectedIndex = this.chartDays.length - 1;
            }
            this.isLoading = false;
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message);
            console.log("Error project-summary-overview: ", error?.code);
          });
    }
  },
  mounted() {
    this.readData();
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.summary-overview {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 320px;
}

/* KPI tiles in one row (the .omx-stat-row grid is scoped to OmxStatTile, so
   it can't reach this wrapper — own it here). */
.summary-overview__kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 0;
}

.summary-overview__mid {
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(220px, 3fr);
  gap: 16px;
}

/* ---------- daily profit chart ---------- */
.chart__plot {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 150px;
  padding: 4px 2px 0;
  border-bottom: 1px solid $omx-ink-100;
}
.chart__bar {
  flex: 1;
  min-width: 6px;
  border-radius: 5px 5px 0 0;
  background: $omx-brand-500;
  opacity: .85;
  cursor: pointer;
  position: relative;
  transition: opacity .12s ease, background .12s ease;
  &:hover { opacity: 1; }
  &--neg { background: $omx-danger-600; }
  &--sel {
    background: $omx-brand-600;
    opacity: 1;
    box-shadow: 0 0 0 2px $omx-brand-100;
  }
  &--sel.chart__bar--neg { background: $omx-danger-600; }
}
.chart__warn {
  position: absolute;
  top: -22px;
  left: 50%;
  transform: translateX(-50%);
}
.chart__axis {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  font-size: 11px;
  color: $omx-ink-500;
}

/* ---------- day detail (full-width card below the row) ---------- */
.summary-overview__day-card ::v-deep .omx-card__body { padding-top: 2px; }
.day-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.day-detail__date { font-weight: 700; margin-right: 8px; }
.day-detail__profit { font-size: 16px; }
.day-detail__task {
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 16px;
  padding: 7px 0;
  font-size: 13px;
  border-top: 1px dashed $omx-ink-100;
}
.day-detail__task-title { font-weight: 600; }

/* ---------- hours health ---------- */
.health {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 8px 0 16px;
}
.health__ring {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.health__ring-inner {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: $omx-surface;
  display: grid;
  place-items: center;
}
.health__ring-num { font-weight: 800; font-size: 16px; font-variant-numeric: tabular-nums; }
.health__row { font-size: 13px; margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
.health__chip {
  display: inline-grid;
  place-items: center;
  min-width: 24px;
  height: 22px;
  padding: 0 6px;
  border-radius: 7px;
  font-weight: 700;
  font-size: 12px;
}
.health__chip--good { background: $omx-success-100; color: $omx-success-600; }
.health__chip--bad  { background: $omx-danger-100;  color: $omx-danger-600; }

.health__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid $omx-ink-100;
}
.health__stat { display: flex; flex-direction: column; gap: 3px; text-align: center; }
.health__stat-val { font-weight: 800; font-size: 15px; font-variant-numeric: tabular-nums; }
.health__stat-lab {
  font-size: 10px;
  color: $omx-ink-500;
  text-transform: uppercase;
  letter-spacing: .04em;
}

/* ---------- task table cells ---------- */
.task-title { font-weight: 600; }
.cellbar { display: flex; align-items: center; gap: 8px; justify-content: flex-end; }
.cellbar__track {
  width: 96px;
  height: 7px;
  border-radius: 999px;
  background: $omx-ink-100;
  overflow: hidden;
}
.cellbar__fill { display: block; height: 100%; border-radius: 999px; }
.cellbar__fill--brand { background: $omx-brand-600; }
.cellbar__pct { font-variant-numeric: tabular-nums; min-width: 38px; text-align: right; }
.cell-sep { color: $omx-ink-300; margin: 0 4px; }

.statuschip {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 9px;
  border-radius: 8px;
}
.statuschip--good { background: $omx-success-100; color: $omx-success-600; }
.statuschip--warn { background: $omx-accent-100;  color: $omx-accent-600; }
.statuschip--bad  { background: $omx-danger-100;  color: $omx-danger-600; }

.summary-overview__empty {
  color: $omx-ink-500;
  text-align: center;
  padding: 28px 0;
}

@media (max-width: 900px) {
  .summary-overview__mid { grid-template-columns: 1fr; }
  .summary-overview__kpis { grid-template-columns: repeat(2, 1fr); }
}
</style>
